import { AUTH } from '@concord/config';
import { Button, Dialog } from '@concord/ui';
import { useEffect, useRef, useState } from 'react';
import { api, signInPath } from './api';
import { useMe, useSignOut } from './session';

const IDLE_MS = AUTH.idleMinutes * 60_000;
const WARN_MS = IDLE_MS - 60_000;
const KEY = 'concord.lastActivity';
const EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'] as const;

/** Shared across tabs, so working in one tab keeps the others signed in. */
function lastActivity(local: number): number {
  try {
    return Math.max(local, Number(localStorage.getItem(KEY) ?? 0));
  } catch {
    return local;
  }
}

/**
 * Signs out after AUTH.idleMinutes without keyboard, pointer or scroll activity in any tab,
 * clearing the screen of patient data; warns a minute before. The server enforces the same
 * limit on its side, so a closed laptop is signed out too.
 */
export function IdleGuard() {
  const { data: me } = useMe();
  const signOutNow = useSignOut();
  // A fresh function each render; the timer must not restart on every render.
  const signOut = useRef(signOutNow);
  signOut.current = signOutNow;
  const last = useRef(Date.now());
  const accountId = me?.accountId;
  const [warning, setWarning] = useState(false);

  useEffect(() => {
    if (!accountId) return;
    last.current = Date.now();
    let written = 0;
    const touch = () => {
      const now = Date.now();
      last.current = now;
      if (now - written > 5_000) {
        written = now;
        try {
          localStorage.setItem(KEY, String(now));
        } catch {
          // Storage blocked: this tab still keeps its own clock.
        }
      }
    };
    touch();
    for (const e of EVENTS) window.addEventListener(e, touch, { passive: true });
    const tick = setInterval(() => {
      const idle = Date.now() - lastActivity(last.current);
      if (idle >= IDLE_MS) {
        clearInterval(tick);
        void signOut.current(signInPath('idle'));
      } else {
        setWarning(idle >= WARN_MS);
      }
    }, 5_000);
    return () => {
      clearInterval(tick);
      for (const e of EVENTS) window.removeEventListener(e, touch);
    };
  }, [accountId]);

  if (!me) return null;
  return (
    <Dialog
      open={warning}
      onOpenChange={(v) => !v && setWarning(false)}
      title="Still there?"
      description={`For patient privacy, Concord signs you out after ${AUTH.idleMinutes} minutes without activity. You will be signed out in under a minute.`}
      footer={
        <>
          <Button
            variant="sec"
            onClick={() => void signOut.current(me.realm === 'staff' ? '/console/sign-in' : '/sign-in')}
          >
            Sign out now
          </Button>
          <Button
            onClick={async () => {
              last.current = Date.now();
              setWarning(false);
              await api('/me').catch(() => undefined);
            }}
          >
            Stay signed in
          </Button>
        </>
      }
    />
  );
}
